/*
 Copyright (c) 2026 WSO2 LLC. (http://www.wso2.com) All Rights Reserved.

 WSO2 LLC. licenses this file to you under the Apache License,
 Version 2.0 (the "License"); you may not use this file except
 in compliance with the License.
 You may obtain a copy of the License at

 http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing,
 software distributed under the License is distributed on an
 "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 KIND, either express or implied.  See the License for the
 specific language governing permissions and limitations
 under the License.
*/

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useColorScheme } from '@wso2/oxygen-ui';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Drawer,
  IconButton,
  Button,
} from '@wso2/oxygen-ui';
import { ChevronRight, Filter } from '@wso2/oxygen-ui-icons-react';
import { BallerinaPackage, FilterOptions } from '@/types/connector';
import {
  searchPackages,
  fetchFiltersProgressively,
  refreshCatalog,
  getFacetCounts,
  SortOption,
  FacetCounts,
} from '@/lib/rest-client';
import Pagination from '@/components/Pagination';
import ConnectorCard from '@/components/ConnectorCard';
import WSO2Header from '@/components/WSO2Header';
import Hero from '@/components/Hero';
import FilterSidebar from '@/components/FilterSidebar';
import SearchBar from '@/components/SearchBar';
import Footer from '@/components/Footer';
import SelectedFilters from '@/components/SelectedFilters';

// WSO2 brand colors
const WSO2_ORANGE = '#FF7300';

// Valid sort options for validation
const VALID_SORT_OPTIONS: SortOption[] = [
  'name-asc',
  'name-desc',
  'pullCount-desc',
  'pullCount-asc',
  'date-desc',
  'date-asc',
];

// Default values
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 30;
const DEFAULT_SORT: SortOption = 'pullCount-desc';
const MAX_PAGE_SIZE = 100;

/**
 * Validate and parse page number from URL parameter
 */
function parsePageParam(value: string | null): number {
  if (!value) return DEFAULT_PAGE;
  const parsed = parseInt(value, 10);
  if (!Number.isFinite(parsed) || Number.isNaN(parsed) || parsed < 1) {
    return DEFAULT_PAGE;
  }
  return Math.floor(parsed);
}

/**
 * Validate and parse page size from URL parameter
 */
function parsePageSizeParam(value: string | null): number {
  if (!value) return DEFAULT_PAGE_SIZE;
  const parsed = parseInt(value, 10);
  if (!Number.isFinite(parsed) || Number.isNaN(parsed) || parsed < 1) {
    return DEFAULT_PAGE_SIZE;
  }
  return Math.min(Math.max(1, Math.floor(parsed)), MAX_PAGE_SIZE);
}

/**
 * Validate sort option from URL parameter
 */
function parseSortParam(value: string | null): SortOption {
  if (!value) return DEFAULT_SORT;
  if (VALID_SORT_OPTIONS.includes(value as SortOption)) {
    return value as SortOption;
  }
  return DEFAULT_SORT;
}

export default function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { mode } = useColorScheme();

  // Handle system mode by detecting actual system preference
  const [effectiveMode, setEffectiveMode] = useState<'light' | 'dark'>(() => {
    if (mode === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return mode as 'light' | 'dark';
  });

  // Update effectiveMode when mode changes and subscribe to OS theme changes if mode is 'system'
  useEffect(() => {
    let m: MediaQueryList | null = null;
    let handler: ((e: MediaQueryListEvent) => void) | null = null;
    if (mode === 'system') {
      m = window.matchMedia('(prefers-color-scheme: dark)');
      setEffectiveMode(m.matches ? 'dark' : 'light');
      handler = (e: MediaQueryListEvent) => {
        setEffectiveMode(e.matches ? 'dark' : 'light');
      };
      if (m.addEventListener) {
        m.addEventListener('change', handler);
      } else if (m.addListener) {
        m.addListener(handler);
      }
    } else {
      setEffectiveMode(mode as 'light' | 'dark');
    }
    return () => {
      if (m && handler) {
        if (m.removeEventListener) {
          m.removeEventListener('change', handler);
        } else if (m.removeListener) {
          m.removeListener(handler);
        }
      }
    };
  }, [mode]);

  const [connectors, setConnectors] = useState<BallerinaPackage[]>([]);
  const predefinedAreas = [
    'AI & Machine Learning',
    'Cloud & Infrastructure',
    'Communication',
    'CRM & Sales',
    'Data Formats & Utilities',
    'Database',
    'Developer Tools',
    'E-Commerce',
    'ERP & Business Operations',
    'Finance & Accounting',
    'Healthcare',
    'HRMS',
    'Marketing & Social Media',
    'Messaging',
    'Network & Protocols',
    'Productivity & Collaboration',
    'Security & Identity',
    'Storage & File Management',
  ];
  const predefinedTypes = [
    'Connector',
    'Data Loader',
    'Driver',
    'Embedding Provider',
    'Knowledge Base',
    'Library',
    'Model Provider',
    'Short Term Memory Store',
    'Trigger',
    'Vector Store',
  ];
  const [filterOptions, setFilterOptionsState] = useState<FilterOptions>({
    areas: predefinedAreas,
    vendors: [],
    types: predefinedTypes,
  });
  const setFilterOptions = (filters: FilterOptions) => {
    setFilterOptionsState({ ...filters, areas: predefinedAreas, types: predefinedTypes });
  };
  // How many results each filter option would give; null until known (or with no priority catalogue)
  const [facetCounts, setFacetCounts] = useState<FacetCounts | null>(null);
  // Bumped when the live check changes the catalog, so the counts are recomputed
  const [catalogRevision, setCatalogRevision] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  // Search input is immediate (for responsive typing), debouncedQuery drives API calls
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const [debouncedQuery, setDebouncedQuery] = useState(() => searchParams.get('search') || '');

  // Debounce search input by 300ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const [selectedAreas, setSelectedAreas] = useState<string[]>(
    () => searchParams.get('areas')?.split(',').filter(Boolean) || []
  );
  const [selectedVendors, setSelectedVendors] = useState<string[]>(
    () => searchParams.get('vendors')?.split(',').filter(Boolean) || []
  );
  const [selectedTypes, setSelectedTypes] = useState<string[]>(
    () => searchParams.get('types')?.split(',').filter(Boolean) || []
  );
  const [currentPage, setCurrentPage] = useState(() => parsePageParam(searchParams.get('page')));
  const [pageSize, setPageSize] = useState(() => parsePageSizeParam(searchParams.get('size')));
  const [sortBy, setSortBy] = useState<SortOption>(() => parseSortParam(searchParams.get('sort')));

  // Ref to discard stale fetch responses
  const fetchIdRef = useRef(0);
  // Ref to track if initial fetch is done
  const initialFetchDoneRef = useRef(false);
  // Ref to prevent URL->State sync from running (when URL changes from external source)
  const syncingFromUrlRef = useRef(false);
  // Ref to prevent State->URL updates from triggering URL->State sync
  const updatingUrlFromStateRef = useRef(false);
  // Ref to track previous URL params
  const prevSearchParamsRef = useRef<string>('');
  // Ref to mark that the next State->URL update came from a direct Pagination click (push, not replace)
  const isUserPageClickRef = useRef(false);

  // Mobile filter drawer state
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Toggle filter selection
  const toggleAreaFilter = (area: string) => {
    setSelectedAreas((prev) =>
      prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]
    );
    setCurrentPage(1);
  };

  const toggleTypeFilter = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
    setCurrentPage(1);
  };

  const toggleVendorFilter = (vendor: string) => {
    setSelectedVendors((prev) =>
      prev.includes(vendor) ? prev.filter((v) => v !== vendor) : [...prev, vendor]
    );
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setSelectedAreas([]);
    setSelectedTypes([]);
    setSelectedVendors([]);
    setSearchInput('');
    setCurrentPage(1);
  };

  // User-driven search input change (resets to page 1, unlike the URL->State sync below)
  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
    setCurrentPage(1);
  };

  // Direct Pagination click: creates a history entry so browser Back returns to the previous page
  const handlePageChange = (page: number) => {
    isUserPageClickRef.current = true;
    setCurrentPage(page);
  };

  // Fetch page data from REST API
  const fetchPageData = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    try {
      setLoading(true);
      setError(null);

      const request = (page: number) =>
        searchPackages({
          query: debouncedQuery,
          areas: selectedAreas,
          vendors: selectedVendors,
          types: selectedTypes,
          offset: (page - 1) * pageSize,
          limit: pageSize,
          sort: sortBy,
        });

      let response = await request(currentPage);

      // Discard stale responses
      if (fetchId !== fetchIdRef.current) return;

      // Clamp an out-of-range page (e.g. ?page=999) to the last page
      const lastPage = Math.max(1, Math.ceil(response.count / pageSize));
      if (currentPage > lastPage) {
        response = await request(lastPage);
        if (fetchId !== fetchIdRef.current) return;
        setCurrentPage(lastPage);
      }

      setConnectors(response.packages);
      setTotalCount(response.count);
      setLoading(false);
    } catch (error) {
      if (fetchId !== fetchIdRef.current) return;
      console.error('Failed to fetch page data:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to load connectors. Please refresh the page or try again later.';
      setError(errorMessage);
      setLoading(false);
    }
  }, [
    debouncedQuery,
    selectedAreas,
    selectedVendors,
    selectedTypes,
    currentPage,
    pageSize,
    sortBy,
  ]);

  // Bug fix: Prevents a redundant double-fetch on mount. Effect A fetches data
  // and sets initialLoading=false. This ref flags Effect B to ignore that specific
  // state change without breaking subsequent fetches (filters, sorting, etc.).
  const skipInitialLoadingFetchRef = useRef(false);

  // The latest fetchPageData, so work that finishes after mount re-fetches with the
  // current filters and page rather than the ones captured when it started.
  const fetchPageDataRef = useRef(fetchPageData);
  fetchPageDataRef.current = fetchPageData;

  // Load filter options once on mount, then load first page
  useEffect(() => {
    const initialize = async () => {
      try {
        setInitialLoading(true);

        // Phase 1: Load first page immediately (fast)
        await fetchPageData();
        initialFetchDoneRef.current = true;

        // Phase 2: Get filter options (cached or progressive)
        const filters = await fetchFiltersProgressively(undefined, (updatedFilters) => {
          // Background update when complete filters are fetched
          setFilterOptions(updatedFilters);
        });
        setFilterOptions(filters);

        skipInitialLoadingFetchRef.current = true;
        setInitialLoading(false);

        // Packages released since the build arrive through a live check that never
        // blocks the first render; show them once it lands.
        Promise.resolve(refreshCatalog()).then((changed) => {
          if (!changed) return;
          fetchPageDataRef.current();
          setCatalogRevision((revision) => revision + 1);
        });
      } catch (error) {
        console.error('Failed to initialize:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load filters.';
        setError(errorMessage);
        skipInitialLoadingFetchRef.current = true;
        setInitialLoading(false);
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger server-side fetch when filters/sort/page change (after initial loading)
  useEffect(() => {
    if (!initialLoading) {
      if (skipInitialLoadingFetchRef.current) {
        skipInitialLoadingFetchRef.current = false;
        return;
      }
      fetchPageData();
    }
  }, [initialLoading, fetchPageData]);

  // Keep the sidebar's options in step with the results: recount whenever the filters or
  // the query change. It reads the in-memory catalog, so it never delays the grid.
  useEffect(() => {
    if (initialLoading) return;
    let stale = false;
    getFacetCounts({
      query: debouncedQuery,
      areas: selectedAreas,
      vendors: selectedVendors,
      types: selectedTypes,
    })
      .then((counts) => {
        if (!stale) setFacetCounts(counts);
      })
      .catch(() => {
        if (!stale) setFacetCounts(null);
      });
    return () => {
      stale = true;
    };
  }, [
    initialLoading,
    debouncedQuery,
    selectedAreas,
    selectedVendors,
    selectedTypes,
    catalogRevision,
  ]);

  // Sync URL params to state (URL -> State)
  useEffect(() => {
    // Skip if URL was just updated by state
    if (updatingUrlFromStateRef.current) {
      updatingUrlFromStateRef.current = false;
      prevSearchParamsRef.current = searchParams.toString();
      return;
    }

    const currentParamsString = searchParams.toString();

    // Only update if URL actually changed
    if (currentParamsString === prevSearchParamsRef.current) {
      return;
    }

    prevSearchParamsRef.current = currentParamsString;
    syncingFromUrlRef.current = true;

    const page = parsePageParam(searchParams.get('page'));
    const size = parsePageSizeParam(searchParams.get('size'));
    const sort = parseSortParam(searchParams.get('sort'));
    const search = searchParams.get('search') || '';
    const areas = searchParams.get('areas')?.split(',').filter(Boolean) || [];
    const vendors = searchParams.get('vendors')?.split(',').filter(Boolean) || [];
    const types = searchParams.get('types')?.split(',').filter(Boolean) || [];
    setCurrentPage(page);
    setPageSize(size);
    setSortBy(sort);
    setSearchInput(search);
    setDebouncedQuery(search);
    setSelectedAreas(areas);
    setSelectedVendors(vendors);
    setSelectedTypes(types);

    // Reset flag after React's state batching completes
    queueMicrotask(() => {
      syncingFromUrlRef.current = false;
    });
  }, [searchParams]);

  // Sync state to URL params (State -> URL)
  useEffect(() => {
    // Read and clear unconditionally so a bail-out below can't leak a stale push flag
    const userPageClick = isUserPageClickRef.current;
    isUserPageClickRef.current = false;

    // Skip if we're currently syncing from URL to avoid infinite loop
    if (syncingFromUrlRef.current) {
      return;
    }

    const params = new URLSearchParams();

    // Filters first, then sort and paging, so the URL reads naturally
    // (?areas=Finance&page=3) and a shared link keeps the same shape.
    if (debouncedQuery) params.set('search', debouncedQuery);
    if (selectedAreas.length > 0) params.set('areas', selectedAreas.join(','));
    if (selectedVendors.length > 0) params.set('vendors', selectedVendors.join(','));
    if (selectedTypes.length > 0) params.set('types', selectedTypes.join(','));
    if (sortBy !== 'pullCount-desc') params.set('sort', sortBy);
    if (pageSize !== 30) params.set('size', pageSize.toString());
    if (currentPage > 1) params.set('page', currentPage.toString());

    const newParamsString = params.toString();
    const currentParamsString = searchParams.toString();

    // Only update URL if params actually changed (prevents stuck flag on no-op updates)
    if (newParamsString !== currentParamsString) {
      // Mark that we're updating URL from state
      updatingUrlFromStateRef.current = true;
      setSearchParams(params, { replace: !userPageClick });
    }
  }, [
    currentPage,
    pageSize,
    debouncedQuery,
    selectedAreas,
    selectedVendors,
    selectedTypes,
    sortBy,
    searchParams,
    setSearchParams,
  ]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentPage]);

  // Update meta tags dynamically based on search/filters
  useEffect(() => {
    let pageTitle = 'WSO2 Integration Platform Connectors and Pre-built Integrations';
    let description =
      'Discover and integrate with 600+ pre-built Ballerina & MI connectors for popular services and platforms. Browse connectors for AWS, Azure, Google Cloud, Salesforce, Twilio, and more.';

    if (debouncedQuery) {
      pageTitle = `Search: "${debouncedQuery}" - WSO2 Integration Platform Connectors and Pre-built Integrations`;
      description = `Search results for "${debouncedQuery}" - Find Ballerina connectors matching your query.`;
    } else if (selectedAreas.length > 0 || selectedVendors.length > 0 || selectedTypes.length > 0) {
      const filters = [...selectedAreas, ...selectedVendors, ...selectedTypes];
      pageTitle = `${filters.join(', ')} Connectors - WSO2 Integration Platform Connectors and Pre-built Integrations`;
      description = `Browse ${filters.join(', ')} connectors for Ballerina integration.`;
    }

    document.title = pageTitle;

    const updateMetaTag = (selector: string, content: string) => {
      let tag = document.querySelector(selector);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        // Parse selector to create new tag (e.g., "meta[name="robots"]")
        const match = selector.match(/^(\w+)\[(\w+)=["']([^"']+)["']\]$/);
        if (match) {
          const [, tagName, attrName, attrValue] = match;
          tag = document.createElement(tagName);
          tag.setAttribute(attrName, attrValue);
          tag.setAttribute('content', content);
          document.head.appendChild(tag);
        }
      }
    };

    updateMetaTag('meta[name="description"]', description);
    updateMetaTag('meta[name="title"]', pageTitle);
    updateMetaTag('meta[property="og:title"]', pageTitle);
    updateMetaTag('meta[property="og:description"]', description);
    updateMetaTag('meta[name="twitter:title"]', pageTitle);
    updateMetaTag('meta[name="twitter:description"]', description);

    // Set robots meta tag based on hostname
    const robotsContent = window.location.hostname.includes('wso2.com')
      ? 'index, follow'
      : 'noindex';
    updateMetaTag('meta[name="robots"]', robotsContent);
  }, [debouncedQuery, selectedAreas, selectedVendors, selectedTypes]);

  return (
    <>
      {/* Header */}
      <WSO2Header effectiveMode={effectiveMode} />

      {/* Hero Banner */}
      <Hero effectiveMode={effectiveMode} />

      <Box sx={{ minHeight: '100vh' }}>
        {/* Initial Loading State */}
        {initialLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        )}

        {/* Main Content */}
        {!initialLoading && (
          <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
              {/* Sidebar Filters - Hidden on mobile */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <FilterSidebar
                  filterOptions={filterOptions}
                  facetCounts={facetCounts}
                  selectedAreas={selectedAreas}
                  selectedVendors={selectedVendors}
                  selectedTypes={selectedTypes}
                  searchQuery={searchInput}
                  onSearchChange={handleSearchInputChange}
                  onAreaChange={toggleAreaFilter}
                  onVendorChange={toggleVendorFilter}
                  onTypeChange={toggleTypeFilter}
                  onClearAll={clearAllFilters}
                  effectiveMode={effectiveMode}
                />
              </Box>

              {/* Main Content Area */}
              <Box sx={{ flex: 1 }}>
                {/* Error State */}
                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                {/* Mobile Search and Filter Bar - Always visible on mobile */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1, mb: 2.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <SearchBar
                      value={searchInput}
                      onChange={handleSearchInputChange}
                      effectiveMode={effectiveMode}
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<Filter size={16} />}
                    onClick={() => setMobileFilterOpen(true)}
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      borderColor: effectiveMode === 'dark' ? '#ffffff33' : '#E5E7EB',
                      color: effectiveMode === 'dark' ? '#FFFFFF' : '#000000',
                      '&:hover': {
                        borderColor: '#FF7300',
                        bgcolor: 'transparent',
                      },
                    }}
                  >
                    Filter
                  </Button>
                </Box>

                {totalCount === 0 && !loading ? (
                  <>
                    {/* Selected Filters (always show if active) */}
                    <SelectedFilters
                      selectedAreas={selectedAreas}
                      selectedTypes={selectedTypes}
                      selectedVendors={selectedVendors}
                      onAreaDelete={toggleAreaFilter}
                      onTypeDelete={toggleTypeFilter}
                      onVendorDelete={toggleVendorFilter}
                      onClearAll={clearAllFilters}
                      WSO2_ORANGE={WSO2_ORANGE}
                      effectiveMode={effectiveMode}
                    />

                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      minHeight={300}
                    >
                      <Typography variant="h6" gutterBottom>
                        No connectors found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try adjusting your search or filters
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    {/* Top Pagination Bar */}
                    <Paper
                      sx={{
                        p: 1.5,
                        mb: 2.5,
                        bgcolor: effectiveMode === 'dark' ? '#18181B' : '#FFFFFF',
                        border: effectiveMode === 'dark' ? 'none' : '1px solid #E5E7EB',
                        boxShadow:
                          effectiveMode === 'dark' ? 'none' : '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                      }}
                    >
                      <Pagination
                        currentPage={currentPage}
                        totalItems={totalCount}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                        onPageSizeChange={setPageSize}
                        sortBy={sortBy}
                        onSortChange={setSortBy}
                        pageSizeOptions={[10, 30, 50, 100]}
                      />
                    </Paper>

                    {/* Selected Filters (always show if active) */}
                    <SelectedFilters
                      selectedAreas={selectedAreas}
                      selectedTypes={selectedTypes}
                      selectedVendors={selectedVendors}
                      onAreaDelete={toggleAreaFilter}
                      onTypeDelete={toggleTypeFilter}
                      onVendorDelete={toggleVendorFilter}
                      onClearAll={clearAllFilters}
                      WSO2_ORANGE={WSO2_ORANGE}
                      effectiveMode={effectiveMode}
                    />

                    {/* Loading Overlay */}
                    {loading && (
                      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                        <CircularProgress />
                      </Box>
                    )}

                    {/* Cards Grid */}
                    {!loading && (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            lg: 'repeat(3, 1fr)',
                          },
                          gap: 2.5,
                        }}
                      >
                        {connectors.map((connector) => (
                          <ConnectorCard
                            key={`${connector.name}-${connector.version}`}
                            connector={connector}
                            effectiveMode={effectiveMode}
                          />
                        ))}
                      </Box>
                    )}

                    {/* Bottom Pagination Bar */}
                    <Paper
                      sx={{
                        p: 1.5,
                        mt: 3,
                        bgcolor: effectiveMode === 'dark' ? '#18181B' : '#FFFFFF',
                        border: effectiveMode === 'dark' ? 'none' : '1px solid #E5E7EB',
                        boxShadow:
                          effectiveMode === 'dark' ? 'none' : '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                      }}
                    >
                      <Pagination
                        currentPage={currentPage}
                        totalItems={totalCount}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                        onPageSizeChange={setPageSize}
                        sortBy={sortBy}
                        onSortChange={setSortBy}
                        pageSizeOptions={[10, 30, 50, 100]}
                      />
                    </Paper>
                  </>
                )}
              </Box>
            </Box>
          </Container>
        )}
      </Box>

      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="right"
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: '85%',
            maxWidth: 360,
            bgcolor: effectiveMode === 'dark' ? '#18181B' : '#FFFFFF',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
          >
            <Typography variant="h6" fontWeight={600}>
              Filters
            </Typography>
            <IconButton onClick={() => setMobileFilterOpen(false)} size="small">
              <ChevronRight />
            </IconButton>
          </Box>

          <FilterSidebar
            filterOptions={filterOptions}
            facetCounts={facetCounts}
            selectedAreas={selectedAreas}
            selectedVendors={selectedVendors}
            selectedTypes={selectedTypes}
            searchQuery=""
            onSearchChange={() => {}}
            onAreaChange={toggleAreaFilter}
            onVendorChange={toggleVendorFilter}
            onTypeChange={toggleTypeFilter}
            onClearAll={clearAllFilters}
            effectiveMode={effectiveMode}
            hideSearch={true}
          />
        </Box>
      </Drawer>

      {/* Footer */}
      <Footer effectiveMode={effectiveMode} />
    </>
  );
}
